/**
 * Baut die Rümpfe von `POST /documents` und `PATCH /documents/{id}` aus dem
 * Formularzustand des Sheets `document-sheet` — dieselbe Aufteilung wie
 * `features/tasks/task-form.ts`.
 *
 * `PATCH` trägt nur die geänderten Felder, ein geleertes Feld wird direkt
 * als `null` eingetragen — „Direkt `null` statt `clear`" funktioniert im Web
 * für jedes leerbare Feld (Spec 02a, „Felder leeren"; `clear` ist die
 * iOS-Ergänzung). `title` ist nie leerbar (Pflichtfeld, `document-title`
 * bleibt `required`) — ein leeres `title` wird deshalb nie in den Rumpf
 * geschrieben, dieselbe Absicherung trägt bereits die Oberfläche
 * (`canSubmit`).
 *
 * Reine Funktionen, eigenes Modul — leichter zu testen als eingebettet in
 * `DocumentSheet.tsx`.
 *
 * **Seit Spec 24c kein Freitextfeld für den Absender mehr** (Fachregel 1,
 * „Nicht enthalten": „Kein Freitext-Absender mehr in den Clients"):
 * `DocumentFormValues` trägt deshalb kein `sender` mehr — `SenderField` in
 * `DocumentSheet.tsx` verwaltet die Auswahl separat und übergibt `senderId`
 * hier als eigenen Parameter, analog zu `subjectId`. Eine benannte
 * Vertragsänderung, keine Reparatur — bestehende Tests dieses Moduls sind
 * entsprechend angepasst, nicht abgeschwächt (LUK-43, `frontend-dev`-Bericht).
 */

import { parseCents } from '@/lib/money'
import type { DocumentCreate, DocumentUpdate } from '@/features/documents/api'
import type { DocumentTextResult } from '@/features/documents/pdfText'

export interface DocumentFormValues {
  readonly title: string
  /** `''` heißt „kein Datum". */
  readonly documentDate: string
  /** Dezimal-Eingabe wie bei `lib/money.ts::parseCents`, `''` heißt „kein
   * Betrag". */
  readonly amount: string
  /** `''` heißt „keine Art". Bleibt `string`, nicht der literale Enum-Typ —
   * dieselbe Abwägung wie `TaskFormValues.priority`. */
  readonly category: string
  readonly direction: string
}

export interface DocumentUploadedFile {
  readonly storageKey: string
  readonly originalFilename: string
  readonly mimeType: string
  readonly byteSize: number
}

export function buildDocumentCreateBody(
  form: DocumentFormValues,
  file: DocumentUploadedFile,
  subjectId: string | undefined,
  text: DocumentTextResult,
  /** Aus `SenderField` — `undefined` heißt „kein Absender gewählt". */
  senderId?: string,
): DocumentCreate {
  const title = form.title.trim()

  return {
    storage_key: file.storageKey,
    original_filename: file.originalFilename,
    mime_type: file.mimeType,
    byte_size: file.byteSize,
    // `null` heißt Eingang — die Vorbelegung von `document-subject-picker`
    // (Spec 09, „Der Objektpicker").
    subject_id: subjectId ?? null,
    // Leer -> Dateiname ohne Endung (Spec 09, „Rumpf POST"), serverseitig
    // geprüft — der Client rät den Dateinamen ohne Endung nicht selbst nach.
    title: title === '' ? null : title,
    direction: form.direction === '' ? null : form.direction,
    document_date: form.documentDate === '' ? null : form.documentDate,
    sender_id: senderId ?? null,
    category: form.category === '' ? null : form.category,
    amount_cents: parseCents(form.amount),
    text_source: text.textSource,
    text: text.text,
    pages: text.pages,
    // `DocumentCreate.blocks` erwartet ein veränderliches `box`-Array — der
    // Rumpf geht als JSON hinaus, `readonly` ist nur eine Absicherung im
    // Client (`pdfText.ts::DocumentTextBlock`) und hat am Übergang zum
    // generierten Typ keine Entsprechung.
    blocks: text.blocks?.map((block) => ({ page: block.page, text: block.text, box: [...block.box] })) ?? null,
  }
}

/**
 * `senderId`: `undefined` heißt „Absenderfeld nicht berührt" (kein Eintrag
 * im Rumpf, Altbestand bleibt), `null` heißt „auf keinen Absender gesetzt",
 * ein String die neue Auswahl — dieselbe Bedeutung wie das erzeugte
 * `DocumentUpdate.sender_id` selbst, deshalb ohne weitere Übersetzung
 * durchgereicht (Spec 24c, Fachregel 7: Setzen von `sender_id` schreibt den
 * Namen mit **und** nimmt jeden Vorschlag weg — serverseitig, nicht hier).
 */
export function buildDocumentPatchBody(
  form: DocumentFormValues,
  initial: DocumentFormValues,
  senderId?: string | null,
): DocumentUpdate {
  const body: DocumentUpdate = {}

  const title = form.title.trim()
  if (title !== initial.title.trim() && title !== '') body.title = title

  if (senderId !== undefined) body.sender_id = senderId

  if (form.documentDate !== initial.documentDate) {
    body.document_date = form.documentDate === '' ? null : form.documentDate
  }

  if (form.amount !== initial.amount) body.amount_cents = parseCents(form.amount)

  if (form.category !== initial.category) body.category = form.category === '' ? null : form.category

  if (form.direction !== initial.direction) body.direction = form.direction

  return body
}
